import { generatedApi } from './api.generated';
import {
  applyIpFlagUpdate,
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { REGIONS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `Regions` tag type is used for both the collection and individual
 * entities. The list query provides a per-item tag for every region plus a
 * dedicated `{ type: 'Regions', id: 'LIST' }` tag, so that:
 * - updating/deleting a single region invalidates only that region's `{ id }`
 *   tag, which refetches both the affected `getRegion` query and the list (the
 *   list provides that same per-item tag);
 * - creating a region invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated entities.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` /
 * `updateQueryData` workaround in `onQueryStarted`: that was attributed to
 * redux-toolkit#5093, but that issue is about the `isSuccess` flag during
 * pessimistic updates, not tag invalidation — `invalidatesTags` propagates to
 * the list correctly via the per-item tags.
 *
 * A region's IP addresses (SKY-4605) are a separate collection per region,
 * cached under one `{ type: 'RegionIps', id: regionId }` tag: any IP
 * create/update/delete refetches exactly that region's list. Those mutations
 * also invalidate the region's own `{ id }` tag and the regions `LIST`,
 * because the backend mirrors the IP table back into the region row — its
 * legacy `ips` column and `updatedAt` — which both the detail page and the
 * regions list show. `LIST` covers a list that doesn't hold this region's
 * per-item tag.
 *
 * An IP update also patches the cached IP list optimistically with the flags
 * it sends (undone if it fails), so a flag switch follows the click at once
 * instead of after the refetch.
 */
const enhancedRegionsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Regions', 'RegionIps'],
  endpoints: {
    [cfg.listRegions.generated]: {
      providesTags: (result) => provideListTags(result, 'Regions'),
    },
    [cfg.getRegion.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Regions', id: arg.id }],
    },
    [cfg.createRegion.generated]: {
      invalidatesTags: [{ type: 'Regions', id: LIST_TAG_ID }],
    },
    [cfg.updateRegion.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Regions', id: arg.id }],
    },
    [cfg.deleteRegion.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Regions', id: arg.id }],
    },
    [cfg.listRegionIps.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'RegionIps', id: arg.id }],
    },
    [cfg.createRegionIp.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RegionIps', id: arg.id },
        { type: 'Regions', id: arg.id },
        { type: 'Regions', id: LIST_TAG_ID },
      ],
    },
    [cfg.updateRegionIp.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          generatedApi.util.updateQueryData(cfg.listRegionIps.generated, { id: arg.id }, (ips) => {
            const ip = ips.find(({ id }) => id === arg.ipId);
            if (ip) {
              applyIpFlagUpdate(ip, arg.updateRegionIpBody);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          // The caller reports the failure (it unwraps the mutation); here
          // the list only goes back to what is stored.
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RegionIps', id: arg.id },
        { type: 'Regions', id: arg.id },
        { type: 'Regions', id: LIST_TAG_ID },
      ],
    },
    [cfg.deleteRegionIp.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RegionIps', id: arg.id },
        { type: 'Regions', id: arg.id },
        { type: 'Regions', id: LIST_TAG_ID },
      ],
    },
  },
});

export const regionsApi: typeof enhancedRegionsApi = enhancedRegionsApi;

export const hookExports: HookExports<typeof cfg, typeof regionsApi> = createHookExports(
  cfg,
  regionsApi,
);

const endpointExports = createEndpointExports(cfg, regionsApi);

export const regionsApiEndpoints: typeof endpointExports = endpointExports;
